import "./blockDemoGrid.css";
import { DataGrid, useGridApiRef, esES } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import {
	getBlockDemoList,
	toggleDeleteModal,
	toggleNotification
} from "../../../../redux-sagas/actions";
import { fetchBlockDemoColumns } from "../../../../utils/fetchColumns";
import fetchClient from "../../../../services/fetch";
import { getTranslation } from "../../../../utils/getTranslation";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import getUniqueValues from "../../../../utils/getUniqueArrayValues";

const BlockDemoGrid = ({
	openModal,
	isDisabled,
	selectedMultiple,
	setSelectedMultiple,
	rowSelectionModel,
	setRowSelectionModel,
	buildQuery,
	paginationModel,
	handlePaginationChange,
	handleSortChange
}) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const apiRef = useGridApiRef();
	const isLoading = useSelector((state) => state.isLoading);
	const blockDemoListInfo = useSelector((state) => state.blockDemoList);
	const localeText =
		i18next.language === "es" ? esES.components.MuiDataGrid.defaultProps.localeText : undefined;

	const handleEditClick = (params) => {
		setSelectedMultiple([params]);
		openModal("edit");
	};

	const handleDuplicateClick = (params) => {
		setSelectedMultiple([params]);
		openModal("duplicate");
	};

	const handleDelete = (params) =>
		fetchClient()
			.delete(`/blockedvehicles/${params.scheduleId}`)
			.then(() => {
				dispatch(getBlockDemoList(buildQuery()));
				dispatch(
					toggleNotification({
						isOpen: true,
						type: "success",
						content1: getTranslation("The block has been successfully deleted.", t),
						highlight1: "",
						content2: "",
						highlight2: ""
					})
				);
			})
			.catch((err) => console.log(err));

	const handleDeleteClick = (params) => {
		dispatch(
			toggleDeleteModal({
				isOpen: true,
				title: getTranslation("Delete Blocking", t),
				content1: t("sure_delete_block"),
				closeCallback: () => handleDelete(params)
			})
		);
	};

	const handleSelectionChange = (newRowSelectionModel) => {
		setRowSelectionModel(newRowSelectionModel);
		const prevSelectedIds = selectedMultiple.map((item) => item.vehicleId).filter(getUniqueValues);

		const selectedIds = newRowSelectionModel
			.map((item) => item.split(";")[1])
			.filter(getUniqueValues);

		const selectedRows = newRowSelectionModel.map((item) => {
			const split = item.split(";");
			const row = {
				vehicleId: split[1],
				brandName: split[2],
				modelName: split[3],
				vin: split[4]
			};

			return row;
		});

		let addedRows,
			removedIds = [];

		// if selectedIds has more elements, row(s) have been added
		if (selectedIds.length > prevSelectedIds.length) {
			// get the new rows from the selection model that is not on the previously selected rows
			addedRows = selectedRows.filter((item) => !prevSelectedIds.includes(item.vehicleId));
			addedRows.forEach((item) => {
				setSelectedMultiple((prevSelectedMultiple) => prevSelectedMultiple.concat(item));
			});
		} else {
			// get the removed id from selectedMultiple that is not on the newly selected ids
			removedIds = prevSelectedIds.filter((item) => !selectedIds.includes(item));
			removedIds.forEach((id) =>
				setSelectedMultiple((prevSelectedMultiple) =>
					prevSelectedMultiple.filter((item) => item.vehicleId !== id)
				)
			);
		}
	};

	const getRowId = (row) =>
		`${row.scheduleId};${row.vehicleId};${row.brandName};${row.modelName};${row.vin}`;

	return (
		<DataGrid
			localeText={localeText}
			getRowId={(row) => getRowId(row)}
			getRowHeight={() => "auto"}
			className='block'
			sortingMode='server'
			onSortModelChange={(newSortModel) => handleSortChange(newSortModel)}
			columns={fetchBlockDemoColumns(
				handleEditClick,
				handleDuplicateClick,
				handleDeleteClick,
				isDisabled
			).map((item) => ({
				...item,
				headerName: getTranslation(item.headerName, t)
			}))}
			apiRef={apiRef}
			rows={blockDemoListInfo.data.map((item) => ({
				...item,
				reason: getTranslation(item.reason, t)
			}))}
			rowCount={blockDemoListInfo.totalRecords || 0}
			paginationModel={paginationModel}
			loading={isLoading}
			pageSizeOptions={[5, 10, 15, 20]}
			slotProps={{
				pagination: {
					labelRowsPerPage: getTranslation("Rows per page:", t)
				}
			}}
			disableColumnMenu
			paginationMode='server'
			onPaginationModelChange={(paginationModel) => handlePaginationChange(paginationModel)}
			onRowSelectionModelChange={(newRowSelectionModel) =>
				handleSelectionChange(newRowSelectionModel)
			}
			rowSelectionModel={rowSelectionModel}
			checkboxSelection
			disableRowSelectionOnClick
			keepNonExistentRowsSelected
		/>
	);
};

export default BlockDemoGrid;
